import React, { useEffect, useState } from "react";
import { Check, RefreshCw } from "lucide-react";
import { Button } from "../ui/button";
import { apiFetch } from "../../utils/api";
import { idToken } from "../../Services/authService";

async function token() { return await idToken(); }

interface Company {
  id: number;
  name: string;
  code: string;
}

const DATA_TYPES = [
  "Agreements",
  "Customers",
  "InvoiceCustomer",
  "PaymentTerms",
  "Products",
  "Users",
  "Pricing",
  "SalesTeams",
  "SalesOrders",
  "ProductDimensionGroup",
  "Warehouses",
  "TaxGroup",
  "AutomaticCharges",
  "ChargeTypes",
  "ExtendedInterestPercentage",
  "MultiLineDiscount",
  "TotalDiscount",
  "Inventory",
  "ApprovedItems"
].sort();

export function RefreshSubscriptionsSection() {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompanies, setSelectedCompanies] = useState<string[]>([]);
  const [selectedDataTypes, setSelectedDataTypes] = useState<string[]>([]);
  const [reloadLines, setReloadLines] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    token().then(t => apiFetch(t, "/api/companies/all")).then((res: any) => {
      setCompanies(res.items ?? []);
    }).catch(() => {});
  }, []);

  function toggleCompany(code: string) {
    setSelectedCompanies(prev =>
      prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]
    );
  }

  function toggleDataType(dt: string) {
    setSelectedDataTypes(prev =>
      prev.includes(dt) ? prev.filter(d => d !== dt) : [...prev, dt]
    );
  }

  function toggleAllDataTypes() {
    setSelectedDataTypes(prev =>
      prev.length === DATA_TYPES.length ? [] : [...DATA_TYPES]
    );
  }

  async function handleSubmit() {
    if (selectedCompanies.length === 0 || selectedDataTypes.length === 0) return;
    setLoading(true);
    setResult(null);
    setError(null);
    try {
      const t = await token();
      const res = await apiFetch(t, "/api/integration/refresh", {
        method: "POST",
        body: JSON.stringify({
          selectedCompanies,
          selectedDataTypes,
          resendAll: reloadLines,
        }),
      });
      setResult(res.message ?? "Refresh initiated.");
    } catch (e: any) {
      setError(e.message ?? "Request failed.");
    } finally {
      setLoading(false);
    }
  }

  const canSubmit = selectedCompanies.length > 0 && selectedDataTypes.length > 0 && !loading;
  const allDataTypesSelected = selectedDataTypes.length === DATA_TYPES.length && DATA_TYPES.length > 0;

  return (
    <div className="bg-white dark:bg-[#1a1a1a] border border-gray-200 dark:border-gray-700 rounded-lg p-6 space-y-6">
      <div>
        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-1">Refresh D365 Subscriptions</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Select companies and data types to trigger a data refresh from Dynamics 365.
        </p>
      </div>

      {/* Companies */}
      <div>
        <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Companies</p>
        <div className="flex flex-wrap gap-2">
          {companies.map(c => (
            <button
              key={c.code}
              onClick={() => toggleCompany(c.code)}
              className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                selectedCompanies.includes(c.code)
                  ? "bg-[#489D53] text-white border-[#489D53]"
                  : "bg-white dark:bg-[#2a2a2a] text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:border-[#489D53]"
              }`}
            >
              {c.name} <span className="opacity-60 text-xs">({c.code})</span>
            </button>
          ))}
        </div>
      </div>

      {/* Data Types */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Data Types</p>
          <button
            type="button"
            role="checkbox"
            aria-checked={allDataTypesSelected}
            onClick={toggleAllDataTypes}
            className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-sm transition-colors ${
              allDataTypesSelected
                ? "bg-[#489D53] text-white border-[#489D53]"
                : "bg-white dark:bg-[#2a2a2a] text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:border-[#489D53]"
            }`}
          >
            <span
              className={`inline-flex h-4 w-4 items-center justify-center rounded border ${
                allDataTypesSelected
                  ? "border-white bg-white/20"
                  : "border-gray-400 dark:border-gray-500 bg-transparent"
              }`}
            >
              {allDataTypesSelected ? <Check className="h-3 w-3" /> : null}
            </span>
            Select all
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {DATA_TYPES.map(dt => (
            <button
              key={dt}
              onClick={() => toggleDataType(dt)}
              className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                selectedDataTypes.includes(dt)
                  ? "bg-[#489D53] text-white border-[#489D53]"
                  : "bg-white dark:bg-[#2a2a2a] text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:border-[#489D53]"
              }`}
            >
              {dt}
            </button>
          ))}
        </div>
      </div>

      {/* Reload Lines toggle */}
      <div className="flex items-center gap-3">
        <button
          role="switch"
          aria-checked={reloadLines}
          onClick={() => setReloadLines(v => !v)}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
            reloadLines ? "bg-[#489D53]" : "bg-gray-300 dark:bg-gray-600"
          }`}
        >
          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${reloadLines ? "translate-x-6" : "translate-x-1"}`} />
        </button>
        <span className="text-sm text-gray-700 dark:text-gray-300">
          Reload Lines <span className="text-xs text-gray-400">(resendAll)</span>
        </span>
      </div>

      {/* Submit */}
      <div className="flex items-center gap-4">
        <Button
          onClick={handleSubmit}
          disabled={!canSubmit}
          className="bg-[#489D53] hover:bg-[#3a8a44] text-white"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          {loading ? "Submitting…" : "Trigger Refresh"}
        </Button>
        {selectedCompanies.length === 0 || selectedDataTypes.length === 0 ? (
          <p className="text-xs text-gray-400">Select at least one company and one data type.</p>
        ) : null}
      </div>

      {result && <p className="text-sm text-green-600 dark:text-green-400">{result}</p>}
      {error && <p className="text-sm text-red-500">{error}</p>}
    </div>
  );
}
